// src/modules/app-views/goodsReceipt/pages/Document/modals/ItemModal/Step1.jsx
// *******************************************************************************
// GOODS RECEIPT ITEM MODAL - STEP 1 (NF ITEM DATA)
// *******************************************************************************
// Captures supplier's item data from the NF-e:
// Line 1: Supplier SKU + Description* + Purchase Unit*
// Line 2: NCM + CFOP + Tax Situation (hidden for NFC-e)
// Line 3: Quantity* + Unit Value* + Discount + Total
// Line 4: Unit Conversion (Sale Unit* + Factor* + Converted Qty + Converted Cost)
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useCallback } from 'react';
import { Row, Col, Divider, Typography, Form } from 'antd';
import { ExperimentOutlined, LinkOutlined, SwapOutlined } from '@ant-design/icons';
import SimpleSelectAsyncProducts from 'components/SimpleComponents/SimpleSelectAsyncProducts';
import SimpleSelectAsyncNcm from 'components/SimpleComponents/SimpleSelectAsyncNcm';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleUniqueKey from 'components/SimpleComponents/SimpleUniqueKey';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import SimpleCurrencyPercentage from 'components/SimpleComponents/SimpleCurrencyPercentage';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import financialService from 'services/financialService';
import { CFOP_ENTRY_OPTIONS, ICMS_CST_OPTIONS } from 'modules/app-views/products/constants';
import { API } from 'constants/Api';

const { Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const ProductStepContent = ({
  form,
  onProductSelect,
  onValueChange,
  onOpenParametersModal,
  onUnitChange,
  selectedProduct,
  issuerId,
  isNfce,
  isFactorDisabled,
  autoLinked,
  onAutoLinkedChange,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const unitsOptions = useMemo(() => buildUnitsOptions(storedParameters), [storedParameters]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24}>
        <Divider style={{ margin: '4px 0 8px' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <ExperimentOutlined style={{ marginRight: 4 }} />
            {translateX('supplier_description')}
          </Text>
        </Divider>
      </Col>

      {/* Line 1: Supplier SKU (auto-search) + Description* + Purchase Unit* */}
      <Col xs={24} sm={6}>
        <SimpleUniqueKey
          name="productCode"
          label="supplier_sku"
          searchUrl={API.PRODUCTS.SEARCH_BY_SUPPLIER_SKU}
          searchParams={value => ({ supplierId: issuerId, sku: value })}
          onFound={product => {
            onProductSelect?.(product);
            onAutoLinkedChange?.(true);

            // Fill form with supplier ref + product data
            if (product && form) {
              const supplierRef = findMatchingSupplier(product.suppliers, issuerId);
              fillFormFromProduct(form, product, supplierRef, onValueChange);
            }
          }}
          onNotFound={() => {
            onAutoLinkedChange?.(false);
          }}
          onClear={() => {
            onProductSelect?.(null);
            onAutoLinkedChange?.(false);
          }}
        />
      </Col>

      <Col xs={24} sm={12}>
        <SimpleInput name="description" label="description" required />
      </Col>

      <Col xs={24} sm={6}>
        <SimpleSelect
          name="purchaseUnitUid"
          label="purchase_unit"
          required
          allowClear
          showSearch
          options={unitsOptions}
          showAddButton
          onAddButtonFn={() => onOpenParametersModal?.('units')}
          onChange={v => onUnitChange?.('purchase', v)}
        />
      </Col>

      {/* Line 2: NCM + CFOP + Tax Situation (hidden for NFC-e) */}
      {!isNfce && (
        <>
          <Col xs={24} sm={8}>
            <SimpleSelectAsyncNcm name="ncm" label="ncm" required />
          </Col>

          <Col xs={12} sm={8}>
            <SimpleSelect
              name="cfop"
              label="cfop"
              required
              showSearch
              allowClear
              options={CFOP_ENTRY_OPTIONS}
            />
          </Col>

          <Col xs={12} sm={8}>
            <SimpleSelect
              name="taxSituation"
              label="tax_situation"
              required
              showSearch
              allowClear
              options={ICMS_CST_OPTIONS}
            />
          </Col>
        </>
      )}

      {/* Line 3: Quantity* + Unit Value* + Discount + Total */}
      <Col xs={12} sm={6}>
        <SimpleNumber
          name="quantity"
          label="quantity"
          required
          min={0.001}
          decimalPlaces={3}
          onChange={onValueChange}
        />
      </Col>

      <Col xs={12} sm={6}>
        <SimpleCurrency
          name="unitValue"
          label="unit_value"
          required
          min={0}
          currencies={[{ value: 'BRL', label: 'R$', text: 'Real Brasileiro' }]}
          onChange={onValueChange}
        />
      </Col>

      <Col xs={12} sm={6}>
        <SimpleCurrencyPercentage
          name="discount"
          label="discount"
          base={financialService.roundCurrency(
            (form?.getFieldValue('quantity') || 1) * (form?.getFieldValue('unitValue') || 0)
          )}
          init="percentage"
          currencies={[{ value: 'BRL', label: 'R$', text: 'Real Brasileiro' }]}
          onChange={onValueChange}
        />
      </Col>

      <Col xs={12} sm={6}>
        <SimpleCurrency
          name="totalValue"
          label="total"
          currencies={[{ value: 'BRL', label: 'R$', text: 'Real Brasileiro' }]}
          disabled
        />
      </Col>

      {/* Line 4: Divider "Unit Conversion" */}
      <Col xs={24}>
        <Divider style={{ margin: '4px 0 8px' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <SwapOutlined style={{ marginRight: 4 }} />
            {translateX('unit_conversion')}
          </Text>
        </Divider>
      </Col>

      {/* Line 5: Sale Unit* + Factor* + Converted Qty + Converted Cost */}
      <Col xs={24} sm={6}>
        <SimpleSelect
          name="saleUnitUid"
          label="sale_unit"
          required
          allowClear
          showSearch
          options={unitsOptions}
          showAddButton
          onAddButtonFn={() => onOpenParametersModal?.('units')}
          onChange={v => onUnitChange?.('sale', v)}
        />
      </Col>

      <Col xs={8} sm={6}>
        <SimpleNumber
          name="conversionFactor"
          label="conversion_factor"
          required
          min={isFactorDisabled ? 1 : 0.001}
          decimalPlaces={3}
          disabled={isFactorDisabled}
          onChange={onValueChange}
        />
      </Col>

      <Col xs={8} sm={6}>
        <SimpleNumber name="convertedQuantity" label="converted_qty" decimalPlaces={3} disabled />
      </Col>

      <Col xs={8} sm={6}>
        <SimpleCurrency
          name="convertedUnitCost"
          label="converted_unit_cost"
          currencies={[{ value: 'BRL', label: 'R$', text: 'Real Brasileiro' }]}
          disabled
        />
      </Col>

      <Col xs={24}>
        <Divider style={{ margin: '4px 0 8px' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <LinkOutlined style={{ marginRight: 4 }} />
            {translateX('link_product')}
          </Text>
        </Divider>
      </Col>

      {/* Product search (optional - link to existing product, disabled when auto-linked via supplier SKU) */}
      <Col xs={24}>
        <SimpleSelectAsyncProducts
          name="linkedProductId"
          label="product"
          placeholder="search_product"
          onProductSelect={onProductSelect}
          selectedProduct={selectedProduct}
          environment="purchase"
          issuerId={issuerId}
          required={false}
          disabled={autoLinked}
        />
      </Col>
    </Row>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const productStep = ({
  selectedProduct,
  setSelectedProduct,
  onOpenParametersModal,
  issuerId,
  documentType,
  autoLinked,
  setAutoLinked,
}) => {
  const isNfce = documentType === 'nfce';

  // NFe requires NCM, CFOP, taxSituation; NFC-e hides them
  const baseFields = [
    'description',
    'purchaseUnitUid',
    'quantity',
    'unitValue',
    'saleUnitUid',
    'conversionFactor',
  ];
  const nfeFields = isNfce ? [] : ['ncm', 'cfop', 'taxSituation'];

  return {
    stepTitle: 'goods_receipt_items',
    validationFields: [...baseFields, ...nfeFields],
    content: ({ form }) => {
      const isNfce = documentType === 'nfce';

      // Check if purchase and sale units are the same to auto-lock factor
      const purchaseUnitUid = Form.useWatch('purchaseUnitUid', form);
      const saleUnitUid = Form.useWatch('saleUnitUid', form);
      const sameUnit = purchaseUnitUid && saleUnitUid && purchaseUnitUid === saleUnitUid;

      // Recalculate total, conversion and subtotal when any value changes
      const recalculateTotal = useCallback(() => {
        if (!form) return;

        const qty = form.getFieldValue('quantity') || 1;
        const price = form.getFieldValue('unitValue') || 0;
        const factor = form.getFieldValue('conversionFactor') || 1;
        const discount = form.getFieldValue('discount') || {
          init: 'percentage',
          percentage: 0,
          value: 0,
        };

        const itemSubtotal = financialService.roundCurrency(qty * price);
        const discountValue = discount.value || 0;
        const totalValue = financialService.roundCurrency(
          Math.max(0, itemSubtotal - discountValue)
        );

        const convQty = financialService.roundCurrency(qty * factor);
        const convCost = convQty > 0 ? financialService.roundCurrency(totalValue / convQty) : 0;

        form.setFieldsValue({
          totalValue,
          convertedQuantity: convQty,
          convertedUnitCost: convCost,
        });
      }, [form]);

      // Handle unit change: sync factor when units match
      const handleUnitChange = useCallback(
        (which, value) => {
          if (!form) return;

          const otherField = which === 'purchase' ? 'saleUnitUid' : 'purchaseUnitUid';
          const otherValue = form.getFieldValue(otherField);

          // If both units are now the same, lock factor to 1
          if (value && otherValue && value === otherValue) {
            form.setFieldsValue({ conversionFactor: 1 });
            // Recalculate with factor = 1
            setTimeout(recalculateTotal, 0);
          }
        },
        [form, recalculateTotal]
      );

      return (
        <ProductStepContent
          form={form}
          onValueChange={recalculateTotal}
          onOpenParametersModal={onOpenParametersModal}
          onUnitChange={handleUnitChange}
          selectedProduct={selectedProduct}
          issuerId={issuerId}
          isNfce={isNfce}
          isFactorDisabled={sameUnit}
          autoLinked={autoLinked}
          onAutoLinkedChange={setAutoLinked}
          onProductSelect={product => {
            setSelectedProduct(product);

            if (product && form) {
              // Only set the link — form fields are filled via SimpleUniqueKey (supplier SKU)
              form.setFieldsValue({ linkedProductId: product._id });
            } else if (!product && form) {
              // Clear link when product is deselected
              form.setFieldsValue({ linkedProductId: undefined });
            }
          }}
        />
      );
    },
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default productStep;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build unit options from product parameters
 */
function buildUnitsOptions(storedParameters) {
  const unitsArray = storedParameters?.modules?.products?.units;

  if (!unitsArray || !Array.isArray(unitsArray)) return [];

  return unitsArray
    .filter(item => !item.removed)
    .map(item => {
      const name = item.name || '';
      const description = item.description || '';
      const displayLabel = description ? `${name} - ${description}` : name;

      return {
        value: item.uid,
        text: displayLabel,
        label: name,
      };
    });
}

/**
 * Find matching supplier reference by issuerId (supplierId).
 * Falls back to the primary active supplier if no exact match.
 */
function findMatchingSupplier(suppliers, issuerId) {
  if (!Array.isArray(suppliers) || suppliers.length === 0) return null;

  if (issuerId) {
    const match = suppliers.find(
      s => s.isActive !== false && String(s.supplierId) === String(issuerId),
    );
    if (match) return match;
  }

  return suppliers.find(s => s.isPrimary && s.isActive !== false) || null;
}

/**
 * Fill form fields from product + supplier ref data.
 * Called when a product is found via supplier SKU search.
 */
function fillFormFromProduct(form, product, supplierRef, recalculate) {
  // Sale unit from product catalog
  const saleUnitUid =
    product.unitUid ||
    (typeof product.unit === 'object' && product.unit !== null ? product.unit.uid : null) ||
    null;

  // NCM from product
  const ncm = product.ncm || product.fiscalData?.ncm || null;

  // Supplier data
  const purchaseUnitUid = supplierRef?.unitUid || null;
  const cfop = supplierRef?.cfop || null;
  const taxSituation = supplierRef?.taxSituation || null;
  const purchasePrice = supplierRef?.purchasePrice || 0;
  const factor = supplierRef?.conversionFactor || 1;

  // Check if units match → lock factor to 1
  const unitsMatch = purchaseUnitUid && saleUnitUid && purchaseUnitUid === saleUnitUid;
  const effectiveFactor = unitsMatch ? 1 : factor;

  const quantity = form.getFieldValue('quantity') || 1;
  const totalValue = financialService.roundCurrency(quantity * purchasePrice);
  const convQty = financialService.roundCurrency(quantity * effectiveFactor);
  const convCost = convQty > 0 ? financialService.roundCurrency(totalValue / convQty) : 0;

  form.setFieldsValue({
    linkedProductId: product._id,
    // Supplier data
    description: supplierRef?.description || product.description || product.name,
    purchaseUnitUid,
    cfop,
    taxSituation,
    unitValue: purchasePrice,
    // Product data
    ncm,
    saleUnitUid,
    // Calculated
    quantity,
    totalValue,
    discount: { init: 'percentage', percentage: 0, value: 0 },
    conversionFactor: effectiveFactor,
    convertedQuantity: convQty,
    convertedUnitCost: convCost,
  });

  // Trigger recalculation
  setTimeout(() => recalculate?.(), 0);
}
